import { Button, Typography } from '@mui/material';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getTranslatedValue } from 'src/common/utils/format-utils';
import { DataTable } from 'src/components/data-table';
import { DataTableFooter } from 'src/components/data-table/data-table-footer';
import { ConfirmDialog } from 'src/components/dialogs';
import { PlusIcon } from 'src/components/icons';
import { Page } from 'src/components/page';
import { useAuth, useDialog, useResources } from 'src/hooks';
import { deleteQuestion, getQuestionList } from 'src/thunks/question-thunks';
import { TestsEditDialog } from '../tests-edit-dialog';
import { TestsExpanded } from '../tests-expanded';
import { TestsPanel } from '../tests-panel/tests-panel';
import { TestsTableActions } from '../tests-table-actions';

export const TestsList: ComponentType = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [openTestsEdit, closeTestsEdit] = useDialog(TestsEditDialog);
  const [openConfirm, closeConfirm] = useDialog(ConfirmDialog);
  const locale = i18n.language;
  const { isSuperAdmin } = useAuth();
  const {
    list: { hits, isLoading, total },
    query,
    handleQueryUpdate,
    handleFetchFirstPage,
    handleFetchCurrentPage,
  } = useResources({
    entity: 'question',
    onGetAll: getQuestionList,
    defaultParams: { embed: 'direction,direction.organization' },
  });

  const handleCreateTest = () => {
    openTestsEdit({
      title: t('tests.add', 'Добавление тестов'),
      onSubmit: () => {
        closeTestsEdit();
        handleFetchFirstPage();
      },
    });
  };

  const handleUpdateTest = (id: string | number) => {
    openTestsEdit({
      title: t('tests.edit', 'Редактировать тест'),
      id,
      onSubmit: () => {
        closeTestsEdit();
        handleFetchCurrentPage();
      },
    });
  };

  const handleDeleteTest = (id: string | number) => {
    openConfirm({
      size: 'small',
      resolve: () => {
        closeConfirm();
        deleteQuestion(id)(dispatch).then(() => {
          handleFetchFirstPage();
        });
      },
    });
  };

  const data = {
    hits,
    isLoading,
    expandable: true,
    columns: [
      {
        width: 70,
        label: '№',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <Typography variant="h5" color="primary">
            {item.id}
          </Typography>
        ),
      },
      {
        width: 70,
        label: 'Язык',
        labelKey: 'language',
        renderItem: (item: Record<string, any>) => item.language,
      },
      {
        width: 'auto',
        label: 'Вопрос',
        labelKey: 'question',
        renderItem: (item: Record<string, any>) => item.title,
      },
      {
        width: 200,
        label: 'Организация',
        labelKey: 'organization',
        renderItem: (item: Record<string, any>) => (
          <Typography noWrap maxWidth={200}>
            {getTranslatedValue(item.direction.organization.title, locale)}
          </Typography>
        ),
        hidden: !isSuperAdmin,
      },
      {
        width: 200,
        label: 'Направление',
        labelKey: 'direction',
        renderItem: (item: Record<string, any>) => (
          <Typography noWrap maxWidth={200}>
            {getTranslatedValue(item.direction.title, locale)}
          </Typography>
        ),
        hidden: !isSuperAdmin,
      },
      {
        width: 96,
        label: '',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <TestsTableActions item={item} onUpdate={handleUpdateTest} onDelete={handleDeleteTest} />
        ),
      },
    ],
    expanded: (item: Record<string, any>) => <TestsExpanded item={item} />,
  };

  return (
    <Page
      title={t('tests', 'Тесты')}
      topButton={
        <Button
          onClick={handleCreateTest}
          variant="contained"
          color="secondary"
          startIcon={<PlusIcon />}
          size="small"
        >
          {t('tests.add', 'Добавить тесты')}
        </Button>
      }
    >
      {/* panel */}
      <TestsPanel {...{ query, onUpdate: handleQueryUpdate }} />

      {/* data table */}
      <DataTable {...data} />

      {/* table footer */}
      <DataTableFooter {...{ total, query, onUpdate: handleQueryUpdate }} />
    </Page>
  );
};
